/**
 * Tenant Context Provider
 * Handles multi-tenant context, branding, and theme
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ============================================================================
// Types
// ============================================================================

interface Branding {
  logo_url: string;
  logo_dark_url: string;
  favicon_url: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  danger_color: string;
  warning_color: string;
  success_color: string;
  background_primary: string;
  background_secondary: string;
  text_primary: string;
  text_secondary: string;
  font_family: string;
  font_heading: string;
  border_radius: string;
  company_name: string;
  tagline: string;
  support_email: string;
  footer_text: string;
  show_powered_by: boolean;
}

interface Tenant {
  id: number;
  slug: string;
  name: string;
  tier: 'starter' | 'professional' | 'enterprise';
  branding: Branding;
  features: Record<string, boolean>;
}

interface TenantContextType {
  tenant: Tenant | null;
  branding: Branding;
  isLoading: boolean;
  error: string | null;
  setTenant: (tenant: Tenant) => void;
  updateBranding: (updates: Partial<Branding>) => Promise<void>;
  hasFeature: (feature: string) => boolean;
}

// ============================================================================
// Default Branding
// ============================================================================

const DEFAULT_BRANDING: Branding = {
  logo_url: '/static/logo-default.svg',
  logo_dark_url: '/static/logo-default-dark.svg',
  favicon_url: '/static/favicon.ico',
  primary_color: '#2563eb',
  secondary_color: '#64748b',
  accent_color: '#10b981',
  danger_color: '#ef4444',
  warning_color: '#f59e0b',
  success_color: '#10b981',
  background_primary: '#0f172a',
  background_secondary: '#1e293b',
  text_primary: '#f8fafc',
  text_secondary: '#94a3b8',
  font_family: 'Inter, -apple-system, sans-serif',
  font_heading: 'Inter, -apple-system, sans-serif',
  border_radius: '8px',
  company_name: 'ShopDrawings.AI',
  tagline: 'Professional Shop Drawings',
  support_email: 'support@shopdrawings.ai',
  footer_text: `© ${new Date().getFullYear()} ShopDrawings.AI`,
  show_powered_by: true
};

// ============================================================================
// Context
// ============================================================================

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

// ============================================================================
// Provider
// ============================================================================

interface TenantProviderProps {
  children: React.ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenant, setTenantState] = useState<Tenant | null>(null);
  const [branding, setBranding] = useState<Branding>(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load tenant branding on mount (from domain)
  useEffect(() => {
    loadTenantFromDomain();
  }, []);

  // Apply branding to CSS variables when it changes
  useEffect(() => {
    applyBrandingToCss(branding);
    updateFavicon(branding.favicon_url);
    updatePageTitle(branding.company_name);
  }, [branding]);

  const loadTenantFromDomain = async () => {
    try {
      setIsLoading(true);
      
      // Fetch branding based on current domain
      const response = await fetch(`${API_BASE}/api/tenant/branding`);
      
      if (response.ok) {
        const data = await response.json();
        setBranding({ ...DEFAULT_BRANDING, ...data });
      }
    } catch (err) {
      console.error('Failed to load tenant branding:', err);
      // Use defaults on error
    } finally {
      setIsLoading(false);
    }
  };

  const setTenant = useCallback((newTenant: Tenant) => {
    setTenantState(newTenant);
    if (newTenant.branding) {
      setBranding({ ...DEFAULT_BRANDING, ...newTenant.branding });
    }
  }, []);

  const updateBranding = useCallback(async (updates: Partial<Branding>) => {
    if (!tenant) return;

    try {
      const response = await fetch(`${API_BASE}/api/tenant/branding`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const newBranding = await response.json();
        setBranding({ ...DEFAULT_BRANDING, ...newBranding });
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update branding');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update branding');
      throw err;
    }
  }, [tenant]);

  const hasFeature = useCallback((feature: string): boolean => {
    if (!tenant?.features) return false;
    return tenant.features[feature] === true;
  }, [tenant]);

  const value: TenantContextType = {
    tenant,
    branding,
    isLoading,
    error,
    setTenant,
    updateBranding,
    hasFeature
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

// ============================================================================
// Utility Functions
// ============================================================================

function applyBrandingToCss(branding: Branding) {
  const root = document.documentElement;
  
  const cssMap: Record<string, keyof Branding> = {
    '--primary': 'primary_color',
    '--secondary': 'secondary_color',
    '--accent': 'accent_color',
    '--danger': 'danger_color',
    '--warning': 'warning_color',
    '--success': 'success_color',
    '--bg-primary': 'background_primary',
    '--bg-secondary': 'background_secondary',
    '--text-primary': 'text_primary',
    '--text-secondary': 'text_secondary',
    '--font-family': 'font_family',
    '--font-heading': 'font_heading',
    '--border-radius': 'border_radius'
  };

  Object.entries(cssMap).forEach(([cssVar, brandingKey]) => {
    const value = branding[brandingKey];
    if (value) {
      root.style.setProperty(cssVar, value as string);
    }
  });
}

function updateFavicon(faviconUrl: string) {
  const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement 
    || document.createElement('link');
  link.type = 'image/x-icon';
  link.rel = 'shortcut icon';
  link.href = faviconUrl;
  document.head.appendChild(link);
}

function updatePageTitle(companyName: string) {
  document.title = companyName;
}

// ============================================================================
// Custom Hooks
// ============================================================================

export const useBranding = () => {
  const { branding } = useTenant();
  return branding;
};

export const useFeature = (feature: string) => {
  const { hasFeature } = useTenant();
  return hasFeature(feature);
};

export default TenantProvider;
